/**
 * CommissionsPanel - Admin panel for managing barber commissions
 * Shows earnings report and allows configuring commission rates for cash and card
 */

import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, functions } from '@/lib/firebase';
import { toast } from '@/lib/toast';
import {
 DollarSign,
 TrendingUp,
 Users,
 Percent,
 Settings,
 RefreshCw,
 ChevronDown,
 ChevronUp,
 Save,
 Banknote,
 CreditCard,
} from 'lucide-react';

interface BarberCommission {
 barberId: string;
 barberName: string;
 userId: string;
 commissionRate: number | null; // Legacy - null means using franchise default
 commissionRateCash: number | null;
 commissionRateCard: number | null;
 totalEarnings: number;
 totalCommission: number;
 servicesCount: number;
}

interface FranchiseData {
 franchiseId: string;
 name: string;
 defaultCommissionRate: number;
 defaultCommissionRateCash: number;
 defaultCommissionRateCard: number;
}

interface CommissionsPanelProps {
 selectedFranchiseId: string;
 selectedBranchId: string;
}

const CommissionsPanel: React.FC<CommissionsPanelProps> = ({
 selectedFranchiseId,
 selectedBranchId,
}) => {
 const [loading, setLoading] = useState(true);
 const [barbers, setBarbers] = useState<BarberCommission[]>([]);
 const [franchise, setFranchise] = useState<FranchiseData | null>(null);
 const [period, setPeriod] = useState<'day' | 'week' | 'month' | 'year'>('month');
 const [expandedBarber, setExpandedBarber] = useState<string | null>(null);
 const [editingRate, setEditingRate] = useState<string | null>(null);
 const [newRateCash, setNewRateCash] = useState<string>('');
 const [newRateCard, setNewRateCard] = useState<string>('');
 const [savingRate, setSavingRate] = useState(false);
 const [_reportData, setReportData] = useState<any>(null);
 const [_loadingReport, setLoadingReport] = useState(false);

 // Load franchise data
 useEffect(() => {
 async function loadFranchise() {
 if (selectedFranchiseId === 'all') {
 setFranchise(null);
 return;
 }

 try {
 const franchiseDoc = await getDoc(doc(db, 'franchises', selectedFranchiseId));
 if (franchiseDoc.exists()) {
 const data = franchiseDoc.data();
 setFranchise({
 franchiseId: franchiseDoc.id,
 name: data.name,
 defaultCommissionRate: data.defaultCommissionRate || 0,
 defaultCommissionRateCash: data.defaultCommissionRateCash ?? data.defaultCommissionRate ?? 0,
 defaultCommissionRateCard: data.defaultCommissionRateCard ?? data.defaultCommissionRate ?? 0,
 });
 }
 } catch (error) {
 console.error('Error loading franchise:', error);
 }
 }

 loadFranchise();
 }, [selectedFranchiseId]);

 // Load barbers and their earnings
 useEffect(() => {
 async function loadBarbers() {
 if (selectedFranchiseId === 'all') {
 setBarbers([]);
 setLoading(false);
 return;
 }

 setLoading(true);
 try {
 // Get barbers
 let barbersQuery;
 if (selectedBranchId !== 'all') {
 barbersQuery = query(
 collection(db, 'barbers'),
 where('franchiseId', '==', selectedFranchiseId),
 where('branchId', '==', selectedBranchId)
 );
 } else {
 barbersQuery = query(
 collection(db, 'barbers'),
 where('franchiseId', '==', selectedFranchiseId)
 );
 }

 const barbersSnapshot = await getDocs(barbersQuery);
 const barbersList: BarberCommission[] = [];

 for (const barberDoc of barbersSnapshot.docs) {
 const barberData = barberDoc.data();

 // Skip inactive/deleted barbers
 if (barberData.isActive === false) {
 continue;
 }

 // Get payments for this barber in the selected period
 const periodStart = getPeriodStart(period);
 const paymentsQuery = query(
 collection(db, 'payments'),
 where('barberUserId', '==', barberData.userId),
 where('completedAt', '>=', periodStart)
 );

 const paymentsSnapshot = await getDocs(paymentsQuery);
 let totalEarnings = 0;
 let totalCommission = 0;
 let servicesCount = 0;

 paymentsSnapshot.forEach((paymentDoc) => {
 const payment = paymentDoc.data();
 totalEarnings += payment.servicePrice || 0;
 totalCommission += payment.commissionAmount || 0;
 servicesCount++;
 });

 barbersList.push({
 barberId: barberDoc.id,
 barberName: barberData.displayName || 'Sin nombre',
 userId: barberData.userId,
 commissionRate: barberData.commissionRate ?? null,
 commissionRateCash: barberData.commissionRateCash ?? null,
 commissionRateCard: barberData.commissionRateCard ?? null,
 totalEarnings,
 totalCommission,
 servicesCount,
 });
 }

 // Sort by earnings desc
 barbersList.sort((a, b) => b.totalCommission - a.totalCommission);
 setBarbers(barbersList);
 } catch (error) {
 console.error('Error loading barbers:', error);
 toast.error('Error al cargar los barberos');
 } finally {
 setLoading(false);
 }
 }

 loadBarbers();
 }, [selectedFranchiseId, selectedBranchId, period]);

 // Load franchise report
 useEffect(() => {
 async function loadReport() {
 if (selectedFranchiseId === 'all') {
 setReportData(null);
 return;
 }

 setLoadingReport(true);
 try {
 const getFranchiseReport = httpsCallable(functions, 'getFranchiseEarningsReport');
 const result = await getFranchiseReport({
 franchiseId: selectedFranchiseId,
 period,
 });
 setReportData(result.data);
 } catch (error) {
 console.error('Error loading report:', error);
 } finally {
 setLoadingReport(false);
 }
 }

 loadReport();
 }, [selectedFranchiseId, period]);

 const getPeriodStart = (p: 'day' | 'week' | 'month' | 'year'): Date => {
 const now = new Date();
 switch (p) {
 case 'day':
 return new Date(now.getFullYear(), now.getMonth(), now.getDate());
 case 'week':
 return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
 case 'month':
 return new Date(now.getFullYear(), now.getMonth(), 1);
 case 'year':
 return new Date(now.getFullYear(), 0, 1);
 }
 };

 const getPeriodLabel = () => {
 switch (period) {
 case 'day':
 return 'Hoy';
 case 'week':
 return 'Esta semana';
 case 'month':
 return 'Este mes';
 case 'year':
 return 'Este año';
 }
 };

 const handleUpdateRate = async (targetId: string, type: 'barber' | 'franchise') => {
 const rateCash = newRateCash ? parseFloat(newRateCash) : undefined;
 const rateCard = newRateCard ? parseFloat(newRateCard) : undefined;

 // Validate rates
 if (rateCash !== undefined && (isNaN(rateCash) || rateCash < 0 || rateCash > 100)) {
 toast.error('La comisión en efectivo debe ser un número entre 0 y 100');
 return;
 }
 if (rateCard !== undefined && (isNaN(rateCard) || rateCard < 0 || rateCard > 100)) {
 toast.error('La comisión con tarjeta debe ser un número entre 0 y 100');
 return;
 }

 if (rateCash === undefined && rateCard === undefined) {
 toast.error('Debes introducir al menos una comisión');
 return;
 }

 setSavingRate(true);
 try {
 const updateRate = httpsCallable(functions, 'updateCommissionRate');

 if (type === 'barber') {
 await updateRate({
 targetType: 'barber',
 targetId,
 commissionRateCash: rateCash,
 commissionRateCard: rateCard,
 });

 // Update local state
 setBarbers((prev) =>
 prev.map((b) =>
 b.barberId === targetId
 ? {
 ...b,
 commissionRateCash: rateCash ?? b.commissionRateCash,
 commissionRateCard: rateCard ?? b.commissionRateCard,
 commissionRate: rateCash ?? b.commissionRate,
 }
 : b
 )
 );
 } else {
 // Update franchise default
 await updateRate({
 targetType: 'franchise',
 targetId: selectedFranchiseId,
 commissionRateCash: rateCash,
 commissionRateCard: rateCard,
 });

 setFranchise((prev) =>
 prev
 ? {
 ...prev,
 defaultCommissionRateCash: rateCash ?? prev.defaultCommissionRateCash,
 defaultCommissionRateCard: rateCard ?? prev.defaultCommissionRateCard,
 defaultCommissionRate: rateCash ?? prev.defaultCommissionRate,
 }
 : null
 );
 }

 toast.success('Comisiones actualizadas correctamente');
 } catch (error) {
 console.error('Error updating rate:', error);
 toast.error('Error al actualizar las comisiones');
 } finally {
 setSavingRate(false);
 setEditingRate(null);
 setNewRateCash('');
 setNewRateCard('');
 }
 };

 // Calculate totals
 const totals = barbers.reduce(
 (acc, b) => ({
 earnings: acc.earnings + b.totalEarnings,
 commission: acc.commission + b.totalCommission,
 services: acc.services + b.servicesCount,
 }),
 { earnings: 0, commission: 0, services: 0 }
 );

 // Helper to get effective commission rate
 const getEffectiveRate = (
 barberRate: number | null,
 franchiseRate: number,
 _type: 'cash' | 'card'
 ) => {
 if (barberRate !== null) {
 return { rate: barberRate, isCustom: true };
 }
 return { rate: franchiseRate, isCustom: false };
 };

 if (selectedFranchiseId === 'all') {
 return (
 <div className="bg-surface rounded-lg shadow-sm border border-hairline p-6">
 <div className="text-center py-8">
 <div className="text-4xl mb-4">🏢</div>
 <h3 className="text-lg font-semibold text-ink mb-2">
 Selecciona una franquicia
 </h3>
 <p className="text-ink-muted">
 Para ver las comisiones, selecciona una franquicia específica en el filtro superior.
 </p>
 </div>
 </div>
 );
 }

 return (
 <div className="space-y-6">
 {/* Header with Period Selector */}
 <div className="bg-surface rounded-lg shadow-sm border border-hairline p-6">
 <div className="flex items-center justify-between mb-4">
 <div className="flex items-center gap-3">
 <div className="p-2 bg-success/10 rounded-lg">
 <DollarSign className="w-6 h-6 text-success" />
 </div>
 <div>
 <h2 className="text-xl font-bold text-ink">
 Comisiones y Ganancias
 </h2>
 <p className="text-sm text-ink-muted">
 {franchise?.name || 'Cargando...'}
 </p>
 </div>
 </div>

 {/* Period Selector */}
 <div className="flex gap-2">
 {(['day', 'week', 'month', 'year'] as const).map((p) => (
 <button
 key={p}
 onClick={() => setPeriod(p)}
 className={`px-4 py-2 text-sm rounded-lg font-medium transition ${
 period === p
 ? 'bg-success text-white'
 : 'bg-surface-2 text-ink-muted hover:bg-surface-3'
 }`}
 >
 {p === 'day' ? 'Hoy' : p === 'week' ? 'Semana' : p === 'month' ? 'Mes' : 'Año'}
 </button>
 ))}
 </div>
 </div>

 {/* Franchise Default Rates */}
 <div className="bg-surface-2 rounded-lg p-4">
 <div className="flex items-center gap-3 mb-4">
 <Settings className="w-5 h-5 text-ink-muted" />
 <div>
 <p className="text-sm font-medium text-ink">
 Comisiones por defecto de la franquicia
 </p>
 <p className="text-xs text-ink-muted">
 Se aplican a barberos sin comisión personalizada
 </p>
 </div>
 </div>

 {editingRate === 'franchise' ? (
 <div className="space-y-3">
 <div className="flex items-center gap-3">
 <div className="flex items-center gap-2 flex-1">
 <Banknote className="w-4 h-4 text-success" />
 <span className="text-sm text-ink-muted w-20">Efectivo:</span>
 <input
 type="number"
 value={newRateCash}
 onChange={(e) => setNewRateCash(e.target.value)}
 placeholder={String(franchise?.defaultCommissionRateCash || 0)}
 min="0"
 max="100"
 className="w-20 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink text-sm"
 />
 <span className="text-ink-muted">%</span>
 </div>
 <div className="flex items-center gap-2 flex-1">
 <CreditCard className="w-4 h-4 text-info" />
 <span className="text-sm text-ink-muted w-20">Tarjeta:</span>
 <input
 type="number"
 value={newRateCard}
 onChange={(e) => setNewRateCard(e.target.value)}
 placeholder={String(franchise?.defaultCommissionRateCard || 0)}
 min="0"
 max="100"
 className="w-20 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink text-sm"
 />
 <span className="text-ink-muted">%</span>
 </div>
 </div>
 <div className="flex gap-2 justify-end">
 <button
 onClick={() => handleUpdateRate(selectedFranchiseId, 'franchise')}
 disabled={savingRate}
 className="px-4 py-2 bg-success text-white rounded-lg text-sm hover:bg-success/90 disabled:opacity-50 flex items-center gap-2"
 >
 {savingRate ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
 Guardar
 </button>
 <button
 onClick={() => {
 setEditingRate(null);
 setNewRateCash('');
 setNewRateCard('');
 }}
 className="px-4 py-2 text-ink-muted hover:bg-surface-2 hover:bg-surface-3 rounded-lg text-sm"
 >
 Cancelar
 </button>
 </div>
 </div>
 ) : (
 <div className="flex items-center justify-between">
 <div className="flex gap-6">
 <div className="flex items-center gap-2">
 <Banknote className="w-5 h-5 text-success" />
 <span className="text-sm text-ink-muted">Efectivo:</span>
 <span className="font-bold text-ink">
 {franchise?.defaultCommissionRateCash || 0}%
 </span>
 </div>
 <div className="flex items-center gap-2">
 <CreditCard className="w-5 h-5 text-info" />
 <span className="text-sm text-ink-muted">Tarjeta:</span>
 <span className="font-bold text-ink">
 {franchise?.defaultCommissionRateCard || 0}%
 </span>
 </div>
 </div>
 <button
 onClick={() => {
 setEditingRate('franchise');
 setNewRateCash(String(franchise?.defaultCommissionRateCash || 0));
 setNewRateCard(String(franchise?.defaultCommissionRateCard || 0));
 }}
 className="flex items-center gap-2 px-4 py-2 bg-surface-2 text-ink rounded-lg hover:bg-surface-3 transition text-sm"
 >
 <Percent className="w-4 h-4" />
 Editar
 </button>
 </div>
 )}
 </div>
 </div>

 {/* Summary Cards */}
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <div className="bg-surface-2 rounded-xl p-6 text-ink">
 <div className="flex items-center justify-between mb-2">
 <DollarSign className="w-8 h-8 opacity-80" />
 <span className="text-3xl font-black">{(totals.commission / 100).toFixed(2)}€</span>
 </div>
 <h3 className="text-sm font-medium opacity-90">Total Comisiones Barberos</h3>
 <p className="text-xs opacity-75">{getPeriodLabel()}</p>
 </div>

 <div className="bg-surface-2 rounded-xl p-6 text-ink">
 <div className="flex items-center justify-between mb-2">
 <TrendingUp className="w-8 h-8 opacity-80" />
 <span className="text-3xl font-black">{(totals.earnings / 100).toFixed(2)}€</span>
 </div>
 <h3 className="text-sm font-medium opacity-90">Total Ingresos</h3>
 <p className="text-xs opacity-75">{getPeriodLabel()}</p>
 </div>

 <div className="bg-surface-2 rounded-xl p-6 text-ink">
 <div className="flex items-center justify-between mb-2">
 <Users className="w-8 h-8 opacity-80" />
 <span className="text-3xl font-black">{totals.services}</span>
 </div>
 <h3 className="text-sm font-medium opacity-90">Servicios Completados</h3>
 <p className="text-xs opacity-75">{getPeriodLabel()}</p>
 </div>
 </div>

 {/* Barbers Table */}
 <div className="bg-surface rounded-lg shadow-sm border border-hairline">
 <div className="p-4 border-b border-hairline">
 <h3 className="text-lg font-semibold text-ink">
 Ganancias por Barbero
 </h3>
 </div>

 {loading ? (
 <div className="text-center py-8">
 <div className="animate-spin text-4xl">💈</div>
 <p className="text-ink-muted mt-2">Cargando...</p>
 </div>
 ) : barbers.length === 0 ? (
 <div className="text-center py-8">
 <div className="text-4xl mb-2">👨‍💼</div>
 <p className="text-ink-muted">
 No hay barberos en esta franquicia
 </p>
 </div>
 ) : (
 <div className="divide-y divide-hairline">
 {barbers.map((barber) => {
 const cashRate = getEffectiveRate(
 barber.commissionRateCash,
 franchise?.defaultCommissionRateCash || 0,
 'cash'
 );
 const cardRate = getEffectiveRate(
 barber.commissionRateCard,
 franchise?.defaultCommissionRateCard || 0,
 'card'
 );

 return (
 <div key={barber.barberId} className="p-4">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-4">
 <div className="w-12 h-12 rounded-full bg-surface-2 flex items-center justify-center text-ink font-bold text-lg">
 {barber.barberName.charAt(0).toUpperCase()}
 </div>
 <div>
 <p className="font-semibold text-ink">
 {barber.barberName}
 </p>
 <div className="flex items-center gap-4 text-sm text-ink-muted">
 <span>{barber.servicesCount} servicios</span>
 <span className="flex items-center gap-1">
 <Banknote className="w-3 h-3 text-success" />
 {cashRate.rate}%
 {cashRate.isCustom && (
 <span className="text-xs text-success">(custom)</span>
 )}
 </span>
 <span className="flex items-center gap-1">
 <CreditCard className="w-3 h-3 text-info" />
 {cardRate.rate}%
 {cardRate.isCustom && (
 <span className="text-xs text-info">(custom)</span>
 )}
 </span>
 </div>
 </div>
 </div>

 <div className="flex items-center gap-4">
 <div className="text-right">
 <p className="text-xl font-bold text-success">
 {(barber.totalCommission / 100).toFixed(2)}€
 </p>
 <p className="text-xs text-ink-subtle">
 de {(barber.totalEarnings / 100).toFixed(2)}€
 </p>
 </div>

 <button
 onClick={() =>
 setExpandedBarber(
 expandedBarber === barber.barberId ? null : barber.barberId
 )
 }
 className="p-2 text-ink-muted hover:bg-surface-2 rounded-lg"
 >
 {expandedBarber === barber.barberId ? (
 <ChevronUp className="w-5 h-5" />
 ) : (
 <ChevronDown className="w-5 h-5" />
 )}
 </button>
 </div>
 </div>

 {/* Expanded Section */}
 {expandedBarber === barber.barberId && (
 <div className="mt-4 pt-4 border-t border-hairline">
 <div className="mb-3">
 <p className="text-sm font-medium text-ink mb-1">
 Ajustar comisiones para este barbero
 </p>
 <p className="text-xs text-ink-muted">
 Dejar vacío para usar las comisiones por defecto de la franquicia
 </p>
 </div>

 {editingRate === barber.barberId ? (
 <div className="space-y-3">
 <div className="flex flex-col sm:flex-row gap-3">
 <div className="flex items-center gap-2 flex-1">
 <Banknote className="w-4 h-4 text-success" />
 <span className="text-sm text-ink-muted w-20">Efectivo:</span>
 <input
 type="number"
 value={newRateCash}
 onChange={(e) => setNewRateCash(e.target.value)}
 placeholder="Por defecto"
 min="0"
 max="100"
 className="w-24 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink text-sm"
 />
 <span className="text-ink-muted">%</span>
 </div>
 <div className="flex items-center gap-2 flex-1">
 <CreditCard className="w-4 h-4 text-info" />
 <span className="text-sm text-ink-muted w-20">Tarjeta:</span>
 <input
 type="number"
 value={newRateCard}
 onChange={(e) => setNewRateCard(e.target.value)}
 placeholder="Por defecto"
 min="0"
 max="100"
 className="w-24 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink text-sm"
 />
 <span className="text-ink-muted">%</span>
 </div>
 </div>
 <div className="flex gap-2">
 <button
 onClick={() => handleUpdateRate(barber.barberId, 'barber')}
 disabled={savingRate}
 className="px-4 py-2 bg-success text-white rounded-lg text-sm hover:bg-success/90 disabled:opacity-50 flex items-center gap-2"
 >
 {savingRate ? (
 <RefreshCw className="w-4 h-4 animate-spin" />
 ) : (
 <Save className="w-4 h-4" />
 )}
 Guardar
 </button>
 <button
 onClick={() => {
 setEditingRate(null);
 setNewRateCash('');
 setNewRateCard('');
 }}
 className="px-4 py-2 text-ink-muted hover:bg-surface-2 rounded-lg text-sm"
 >
 Cancelar
 </button>
 </div>
 </div>
 ) : (
 <button
 onClick={() => {
 setEditingRate(barber.barberId);
 setNewRateCash(
 barber.commissionRateCash !== null
 ? String(barber.commissionRateCash)
 : ''
 );
 setNewRateCard(
 barber.commissionRateCard !== null
 ? String(barber.commissionRateCard)
 : ''
 );
 }}
 className="px-4 py-2 bg-surface-2 text-ink rounded-lg hover:bg-surface-3 transition text-sm"
 >
 Editar comisiones
 </button>
 )}
 </div>
 )}
 </div>
 );
 })}
 </div>
 )}
 </div>
 </div>
 );
};

export default CommissionsPanel;
